/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  Component, EventEmitter,
  Input, OnDestroy, OnInit,
  Output
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  filter,
  finalize, map, switchMap, take, takeUntil, tap
} from 'rxjs/operators';

import { StripeCardElement } from '@stripe/stripe-js';
import { Observable, Subject } from 'rxjs';
import { CreditCardDataService, CreditCardStore } from '@sondermind/data-access/client-credit-card';
import { CreditCardForm, CreditCardTokenized } from '@sondermind/utilities/models-credit-card';
import { StripeCardElementBase } from '@sondermind/features/payment-method-form';
import { ConfigurationService, IClientAppConfig } from '@sondermind/configuration';
import { AccountSetupService, BreakpointsService } from '@sondermind/utilities/services';
import { Logger } from '@sondermind/datadog-logging';
import { IrisButtonTypes } from '@sondermind/iris/button';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';
import { ClientInsuranceHttpService, PatientInsuranceStore } from '@sondermind/data-access/client-insurance';
import { BannerMessage } from '@sondermind/core/design/success-error-banner/success-error-banner.component';
import { InfoLinks } from '@sondermind/core/enums/info-links.enum';

@Component({
  selector: 'client-credit-card-add',
  templateUrl: './credit-card-add.component.html',
  styleUrls: ['./credit-card-add.component.scss'],
  standalone: false
})
export class CreditCardAddComponent extends StripeCardElementBase implements OnInit, OnDestroy {
  // used for embedding form in new client account setup flow
  @Input() useInline: boolean = false;
  @Input() useInSessionRequest: boolean = false;
  @Output() closeAdd: EventEmitter<void> = new EventEmitter<void>();
  @Input() isProposeATime: boolean = false;
  @Input() showFinancialAgreement: boolean = false;

  private destroyed$ = new Subject<void>();

  ccForm: UntypedFormGroup;
  financialPolicyForm: UntypedFormGroup;

  submitting: boolean = false;
  addCardStatusCode: number;

  errorBanner: BannerMessage | null = null;
  financialResponsibilityAgreement = InfoLinks.FINANCIAL_RESPONSIBILITY_AGREEMENT;

  get isMobile(): boolean {
    return this.breakpointService.isMobile;
  }
  IrisButtonTypes = IrisButtonTypes;
  IrisButtonTypeAttributeOptions = IrisButtonTypeAttributeOptions;

  constructor(
    logger: Logger,
    configurationService: ConfigurationService<IClientAppConfig>,
    private breakpointService: BreakpointsService,
    private creditCardStore: CreditCardStore,
    private snackbar: MatSnackBar,
    private fb: UntypedFormBuilder,
    private accountSetupService: AccountSetupService,
    private creditCardDataService: CreditCardDataService,
    private patientInsuranceStore: PatientInsuranceStore,
    private clientInsuranceHttp: ClientInsuranceHttpService
  ) {
    super(configurationService.env.app.stripeKey, logger);
  }

  ngOnInit(): void {
    this.loaded$.subscribe((loaded) => {
      if (loaded) {
        this.stripeFormElements = this.mountStripeElements(true);
        this.registerStripeElements(this.stripeFormElements, 'credit-card-add-wrapper');

        this.stripeElementSubject
          .pipe(takeUntil(this.destroyed$))
          .subscribe((registeredElements) => {
            this.saveNewCard(registeredElements);
          });
      }
    });

    this.ccForm = this.fb.group({
      name: ['', Validators.required],
      nickname: ['']
    });

    this.financialPolicyForm = this.fb.group({
      acceptedFinancialPolicy: [false]
    });
  }

  /**
   * Override base class method to include financial agreement validation
   */
  override getStripeFormValidity(): boolean {
    const isBaseValid = super.getStripeFormValidity();
    if (this.showFinancialAgreement) {
      const hasAcceptedPolicy = this.financialPolicyForm.get('acceptedFinancialPolicy')?.value as boolean;
      return isBaseValid && hasAcceptedPolicy;
    }
    return isBaseValid;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * Creates a new credit card on form submission.
   * @param cardElement
   */
  saveNewCard(cardElement: StripeCardElement): void {
    this.startSubmitting();

    const nickname = this.ccForm.controls['nickname']?.value;
    const cardOwnerData: CreditCardForm = {
      name: this.ccForm.controls['name']?.value,
    } as CreditCardForm;

    this.createCard(cardElement, cardOwnerData, nickname, this.accountSetupService.context)
      .pipe(
        finalize(() => this.stopSubmitting())
      ).subscribe({
        next: () => {
          // Add cash pay if hasCashPay is false
          this.patientInsuranceStore.hasCashPay$.pipe(
            take(1),
            filter((hasCashPay) => !hasCashPay),
            switchMap(() => this.clientInsuranceHttp.addCashPay())
          ).subscribe({
            next: () => {
              this.patientInsuranceStore.loadPatientInsuranceList();
            },
            error: () => {
              // Silently ignore errors to not block the card save success flow
            }
          });

          if (!this.useInline) {
            this.snackbar.open('Credit card saved', 'Ok');
          }
          this.closeAdd.emit();
          this.errorBanner = null;
        },
        error: (err) => {
          this.handleCreditCardSaveError(err);
        }
      });
  }

  /**
   * Create a card token via the StripeCardElementBase class and subsequently submit a credit card via the ccHttpService
   *
   * @param card - {StripeCardElement} StripeElement reference from a form
   * @param cardOwnerData { CreditCardForm } - card owner name and other metadata
   * @param nickname { string } - the card nickname
   * @param accountSetupContext { string } - from enum of possible values 'propose_a_time', 'request_to_schedule', or 'independent'
   * @returns Observable<null>
   */
  createCard(
    card: StripeCardElement,
    cardOwnerData: CreditCardForm,
    nickname: string,
    accountSetupContext: string = null
  ): Observable<null> {
    return this.createCardToken(card, cardOwnerData).pipe(
      switchMap((cct: CreditCardTokenized) => this.creditCardDataService.create(
        nickname || `${cct.brand} - ${cct.last4}`,
        cct.token,
        accountSetupContext
      ).pipe(
        tap((res) => {
          this.addCardStatusCode = res.statusCode;
          if (!res.card) {
            throw new Error('No card in response');
          }

          // New cards are marked "active" (default), overriding previous default
          // refresh store to remove default label from old card(s)
          this.creditCardStore.loadCreditCardList();
        }),
        map(() => null),
      ))
    );
  }

  /**
   * Wrapper for better unit tests.
   */
  startSubmitting(): void {
    this.submitting = true;
  }

  /**
   * Wrapper for better unit tests.
   */
  stopSubmitting(): void {
    this.submitting = false;
  }

  handleCreditCardSaveError(err: any) {
    this.snackbar.open('Credit card failed to save', 'Ok');
    this.ccForm.setErrors(err);

    if (this.addCardStatusCode === 422) {
      this.errorBanner = {
        title: 'Your payment method could not be validated',
        description: 'Please double check your payment details below. ' +
          "If they're correct, try contacting your bank for more details."
      };
    }

    if (this.addCardStatusCode === 500) {
      this.errorBanner = {
        title: 'There was an error processing your payment method',
        description: 'Please try again later.'
      };
    }
  }
}
